// skipcq: JS-W1028
/* eslint-disable react-hooks/set-state-in-effect -- session syncs active brewery when permissions or URL change */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { UserContext } from './UserContext';
import { fetchManageableBreweriesForCurrentUser } from '../services/breweryPermissions';
import { subscribeBreweryGroupDisplayNamesById } from '../services/fetchBreweryGroupDisplayNames';
import { formatBreweryMemberLabel } from '../utils/brewery/formatBreweryMemberLabel';
import { resolveBreweryGroupDisplayName } from '../utils/brewery/resolveBreweryGroupDisplayName';
import {
  isAssignedBreweryId,
  resolveInitialActiveBreweryId,
  writeStoredActiveBreweryId,
} from '../utils/brewhouse/activeBreweryStorage';
import {
  encodeBrewhouseScopeValue,
  findBrewhouseScopeOption,
  parseBrewhouseScopeValue,
  readStoredBrewhouseActiveScope,
  resolveValidBrewhouseActiveScope,
  writeStoredBrewhouseActiveScope,
} from '../utils/brewhouse/brewhouseActiveScope';
import {
  buildBrewhouseLocationSelectorOptions,
  shouldShowBrewhouseLocationSelector,
} from '../utils/brewhouse/buildBrewhouseLocationSelectorOptions';

function defaultRefreshBreweries() {
  return Promise.resolve();
}

function defaultSetActiveBreweryId() {
  // Default context noop — real provider supplies setActiveBreweryId.
}

const defaultBrewerySession = {
  breweryId: null,
  breweryGroupId: null,
  activeBreweryName: '',
  activeBreweryCity: '',
  activeBreweryState: '',
  activeLocationLabel: '',
  brandDisplayName: null,
  hasMultipleLocations: false,
  activeScopeValue: '',
  activeScopeLabel: '',
  activeBeerScopeBreweryIds: [],
  showLocationSelector: false,
  locationSelectorOptions: [],
  breweries: [],
  isLoading: true,
  error: null,
  refreshBreweries: defaultRefreshBreweries,
  setActiveBreweryId: defaultSetActiveBreweryId,
  setActiveScopeValue: defaultSetActiveBreweryId,
};

const BrewerySessionContext = React.createContext(defaultBrewerySession);

function findBreweryRow(breweries, breweryId) {
  const normalizedId = typeof breweryId === 'string' ? breweryId.trim() : '';
  if (!normalizedId) {
    return null;
  }
  return (
    breweries.find(
      (row) => typeof row?.breweryId === 'string' && row.breweryId.trim() === normalizedId,
    ) ?? null
  );
}

function BrewerySessionProvider({ children }) {
  const user = useContext(UserContext);
  const location = useLocation();
  const [breweries, setBreweries] = useState([]);
  const [activeBreweryId, setActiveBreweryIdState] = useState(null);
  const [groupDisplayNamesById, setGroupDisplayNamesById] = useState({});
  const [groupDisplayNamesLoaded, setGroupDisplayNamesLoaded] = useState(false);
  const [activeScopeValue, setActiveScopeValueState] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const userId = typeof user.uid === 'string' ? user.uid.trim() : '';

  const refreshBreweries = useCallback(function refreshBreweries() {
    setReloadNonce((nonce) => nonce + 1);
    return Promise.resolve();
  }, []);

  useEffect(() => {
    if (!user.isLoggedIn || user.isBreweryAdmin !== true) {
      setBreweries([]);
      setActiveBreweryIdState(null);
      setGroupDisplayNamesById({});
      setGroupDisplayNamesLoaded(false);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const fetchedRows = await fetchManageableBreweriesForCurrentUser();
        if (!cancelled) {
          setBreweries(fetchedRows);
        }
      } catch (fetchErr) {
        console.error('BrewerySessionProvider: fetch failed', fetchErr);
        if (!cancelled) {
          setError(
            typeof fetchErr?.message === 'string'
              ? fetchErr.message
              : 'Unable to load your breweries. Please try again.',
          );
          setBreweries([]);
          setGroupDisplayNamesById({});
          setGroupDisplayNamesLoaded(false);
          setActiveBreweryIdState(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user.isLoggedIn, user.isBreweryAdmin, reloadNonce]);

  const manageableGroupIds = useMemo(() => {
    const groupIds = new Set();
    breweries.forEach((row) => {
      const groupId =
        typeof row?.breweryGroupId === 'string' ? row.breweryGroupId.trim() : '';
      if (groupId.length > 0) {
        groupIds.add(groupId);
      }
    });
    return Array.from(groupIds);
  }, [breweries]);

  const manageableGroupIdsKey = manageableGroupIds.join('|');

  useEffect(() => {
    const groupIds =
      manageableGroupIdsKey.length > 0 ? manageableGroupIdsKey.split('|') : [];

    if (groupIds.length === 0) {
      setGroupDisplayNamesById({});
      setGroupDisplayNamesLoaded(true);
      return undefined;
    }

    setGroupDisplayNamesLoaded(false);

    const unsubscribe = subscribeBreweryGroupDisplayNamesById(
      groupIds,
      (namesById, loaded) => {
        setGroupDisplayNamesById(namesById);
        setGroupDisplayNamesLoaded(loaded);
      },
    );

    return unsubscribe;
  }, [manageableGroupIdsKey]);

  const requestedBreweryId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const breweryId = queryParams.get('breweryId');
    return typeof breweryId === 'string' ? breweryId.trim() : '';
  }, [location.search]);

  useEffect(() => {
    if (breweries.length === 0) {
      setActiveBreweryIdState(null);
      return;
    }

    if (requestedBreweryId && isAssignedBreweryId(breweries, requestedBreweryId)) {
      setActiveBreweryIdState(requestedBreweryId);
      return;
    }

    setActiveBreweryIdState((previousId) => {
      if (previousId && isAssignedBreweryId(breweries, previousId)) {
        return previousId;
      }
      return resolveInitialActiveBreweryId(breweries, requestedBreweryId, userId);
    });
  }, [breweries, requestedBreweryId, userId]);

  const activeBreweryRow = useMemo(
    () => findBreweryRow(breweries, activeBreweryId),
    [breweries, activeBreweryId],
  );

  const breweryId = useMemo(() => {
    const id =
      typeof activeBreweryRow?.breweryId === 'string'
        ? activeBreweryRow.breweryId.trim()
        : '';
    return id.length > 0 ? id : null;
  }, [activeBreweryRow]);

  const activeBreweryName = useMemo(() => {
    return typeof activeBreweryRow?.breweryName === 'string'
      ? activeBreweryRow.breweryName
      : '';
  }, [activeBreweryRow]);

  const activeBreweryCity = useMemo(() => {
    return typeof activeBreweryRow?.breweryCity === 'string'
      ? activeBreweryRow.breweryCity
      : '';
  }, [activeBreweryRow]);

  const activeBreweryState = useMemo(() => {
    return typeof activeBreweryRow?.breweryState === 'string'
      ? activeBreweryRow.breweryState
      : '';
  }, [activeBreweryRow]);

  const activeLocationLabel = useMemo(() => {
    if (!activeBreweryRow) {
      return '';
    }
    return formatBreweryMemberLabel({
      name: activeBreweryName,
      city: activeBreweryCity,
    });
  }, [activeBreweryRow, activeBreweryName, activeBreweryCity]);

  const breweryGroupId = useMemo(() => {
    const groupId =
      typeof activeBreweryRow?.breweryGroupId === 'string'
        ? activeBreweryRow.breweryGroupId.trim()
        : '';
    return groupId.length > 0 ? groupId : null;
  }, [activeBreweryRow]);

  const brandDisplayName = useMemo(() => {
    if (!breweryGroupId) {
      return null;
    }
    return resolveBreweryGroupDisplayName(groupDisplayNamesById[breweryGroupId]);
  }, [breweryGroupId, groupDisplayNamesById]);

  const hasMultipleLocations = breweries.length > 1;

  const locationSelectorOptions = useMemo(
    () =>
      buildBrewhouseLocationSelectorOptions(
        breweries,
        groupDisplayNamesById,
        groupDisplayNamesLoaded,
      ),
    [breweries, groupDisplayNamesById, groupDisplayNamesLoaded],
  );

  const showLocationSelector = useMemo(
    () => shouldShowBrewhouseLocationSelector(locationSelectorOptions),
    [locationSelectorOptions],
  );

  useEffect(() => {
    if (locationSelectorOptions.length === 0) {
      setActiveScopeValueState('');
      return;
    }

    const requestedScope =
      requestedBreweryId.length > 0 && isAssignedBreweryId(breweries, requestedBreweryId)
        ? encodeBrewhouseScopeValue('location', requestedBreweryId)
        : '';
    if (
      requestedScope.length > 0 &&
      findBrewhouseScopeOption(locationSelectorOptions, requestedScope)
    ) {
      setActiveScopeValueState(requestedScope);
      return;
    }

    const storedScope = userId ? readStoredBrewhouseActiveScope(userId) : null;
    setActiveScopeValueState((previousScope) => {
      if (
        previousScope &&
        findBrewhouseScopeOption(locationSelectorOptions, previousScope)
      ) {
        return previousScope;
      }
      return resolveValidBrewhouseActiveScope(
        storedScope,
        locationSelectorOptions,
        activeBreweryId,
      );
    });
  }, [locationSelectorOptions, userId, activeBreweryId, requestedBreweryId, breweries]);

  const activeScopeOption = useMemo(
    () => findBrewhouseScopeOption(locationSelectorOptions, activeScopeValue),
    [locationSelectorOptions, activeScopeValue],
  );

  const activeScopeLabel = activeScopeOption?.label ?? activeLocationLabel;

  const activeBeerScopeBreweryIds = useMemo(() => {
    if (activeScopeOption?.breweryIds?.length > 0) {
      return activeScopeOption.breweryIds;
    }
    return breweryId ? [breweryId] : [];
  }, [activeScopeOption, breweryId]);

  const setActiveBreweryId = useCallback(
    (nextBreweryId) => {
      const normalizedId =
        typeof nextBreweryId === 'string' ? nextBreweryId.trim() : '';
      if (!normalizedId || !isAssignedBreweryId(breweries, normalizedId)) {
        return;
      }
      setActiveBreweryIdState(normalizedId);
      const nextScope = encodeBrewhouseScopeValue('location', normalizedId);
      setActiveScopeValueState(nextScope);
      if (userId) {
        writeStoredActiveBreweryId(userId, normalizedId);
        writeStoredBrewhouseActiveScope(userId, nextScope);
      }
    },
    [breweries, userId],
  );

  const setActiveScopeValue = useCallback(
    (nextScopeValue) => {
      const normalizedScope =
        typeof nextScopeValue === 'string' ? nextScopeValue.trim() : '';
      const scopeOption = findBrewhouseScopeOption(
        locationSelectorOptions,
        normalizedScope,
      );
      if (!scopeOption) {
        return;
      }

      setActiveScopeValueState(normalizedScope);
      if (userId) {
        writeStoredBrewhouseActiveScope(userId, normalizedScope);
      }

      const parsedScope = parseBrewhouseScopeValue(normalizedScope);
      if (parsedScope?.kind === 'location') {
        const normalizedId = parsedScope.id;
        if (isAssignedBreweryId(breweries, normalizedId)) {
          setActiveBreweryIdState(normalizedId);
          if (userId) {
            writeStoredActiveBreweryId(userId, normalizedId);
          }
        }
        return;
      }

      const anchorBreweryId = scopeOption.breweryIds[0];
      if (anchorBreweryId && isAssignedBreweryId(breweries, anchorBreweryId)) {
        setActiveBreweryIdState(anchorBreweryId);
        if (userId) {
          writeStoredActiveBreweryId(userId, anchorBreweryId);
        }
      }
    },
    [breweries, locationSelectorOptions, userId],
  );

  const value = useMemo(
    () => ({
      breweryId,
      breweryGroupId,
      activeBreweryName,
      activeBreweryCity,
      activeBreweryState,
      activeLocationLabel,
      brandDisplayName,
      hasMultipleLocations,
      activeScopeValue,
      activeScopeLabel,
      activeBeerScopeBreweryIds,
      showLocationSelector,
      locationSelectorOptions,
      breweries,
      isLoading,
      error,
      refreshBreweries,
      setActiveBreweryId,
      setActiveScopeValue,
    }),
    [
      breweryId,
      breweryGroupId,
      activeBreweryName,
      activeBreweryCity,
      activeBreweryState,
      activeLocationLabel,
      brandDisplayName,
      hasMultipleLocations,
      activeScopeValue,
      activeScopeLabel,
      activeBeerScopeBreweryIds,
      showLocationSelector,
      locationSelectorOptions,
      breweries,
      isLoading,
      error,
      refreshBreweries,
      setActiveBreweryId,
      setActiveScopeValue,
    ],
  );

  return (
    <BrewerySessionContext.Provider value={value}>
      {children}
    </BrewerySessionContext.Provider>
  );
}

export { BrewerySessionContext, BrewerySessionProvider, defaultBrewerySession };
